import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileDropzone } from "./components/FileDropzone";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { requestNewRegulator } from "./services/requestNewRegulator";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";

interface IAddRegulatorModalProps {
  setShowAddRegulatorModal: React.Dispatch<React.SetStateAction<boolean>>;
  darkMode: boolean;
}

const AddRegulatorModal: React.FC<IAddRegulatorModalProps> = ({
  setShowAddRegulatorModal,
  darkMode,
}) => {
  const { t } = useTranslation();
  const [isLeftOpen, setIsLeftOpen] = useState(false);
  const [isRightOpen, setIsRightOpen] = useState(false);

  const [descriptionInput, setDescriptionInput] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);

  const { mutate: mutateAddNewUser } = useMutation({
    mutationFn: async (formData: { text: string; files: any }) => {
      return await requestNewRegulator(formData.text, formData.files);
    },
    onSuccess: () => {
      toast.success(t("adminPanel.addRegulatorToaster"));
      setShowAddRegulatorModal(false);
    },
    onError: (error: Error) => {
      console.error("Error requesting a new Regulator:", error);
    },
  });

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    mutateAddNewUser({ text: descriptionInput, files });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-panel w-full max-w-lg max-h-[80vh] relative flex flex-col">
        {/* Header */}
        <div className="p-6 pb-2 flex items-center justify-center relative">
          <h2 className="text-xl title-base-color text-center">
            {t("adminPanel.addRegulatorTitle")}
          </h2>
          <button
            aria-label="Close"
            className="absolute right-3 top-3 modal-close-btn"
            onClick={() => setShowAddRegulatorModal(false)}
            type="button"
          >
            <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="px-6 pt-2 pb-4 overflow-y-auto flex-1">
          {/* Accordion: Instructions */}
          <button
            onClick={() => setIsLeftOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-3 py-2 mb-2 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            <span className="font-semibold text-base text-gray-800 dark:text-gray-200">
              {t("instruction.title")}
            </span>
            {isLeftOpen ? (
              <FiChevronUp
                size={20}
                className="text-gray-600 dark:text-gray-300"
              />
            ) : (
              <FiChevronDown
                size={20}
                className="text-gray-600 dark:text-gray-300"
              />
            )}
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out mb-2 ${
              isLeftOpen ? "max-h-96" : "max-h-0"
            }`}
          >
            <div className="max-h-80 overflow-y-auto px-2 py-1 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 space-y-4 rounded-lg">
              <p className="font-semibold">{t("instruction.userTitle")}</p>

              <p>{t("instruction.text0")}</p>

              <div>
                <p className="font-semibold">1. {t("example.example1Title")}</p>
                <p>{t("instruction.text1")}</p>
              </div>

              <div>
                <p className="font-semibold">2. {t("example.example2Title")}</p>
                <p>{t("instruction.text2")}</p>
              </div>

              <div>
                <p className="font-semibold">3. {t("example.example3Title")}</p>
                <p>{t("instruction.text3")}</p>
              </div>

              <div>
                <p className="font-semibold">4. {t("example.example4Title")}</p>
                <p>{t("instruction.text4")}</p>
              </div>

              <div>
                <p className="font-semibold">5. {t("example.example5Title")}</p>
                <p>{t("instruction.text5")}</p>
              </div>

              <p>{t("instruction.text6")}</p>

              <p className="font-semibold">
                {t("instruction.bestRegards")}
                <br />
                <span className="font-normal">
                  {t("instruction.codetTeam")}
                </span>
              </p>
            </div>
          </div>

          {/* Accordion: Example */}
          <button
            onClick={() => setIsRightOpen((prev) => !prev)}
            className="w-full flex items-center justify-between px-3 py-2 mb-2 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            <span className="font-semibold text-base text-gray-800 dark:text-gray-200">
              {t("example.title")}
            </span>
            {isRightOpen ? (
              <FiChevronUp
                size={20}
                className="text-gray-600 dark:text-gray-300"
              />
            ) : (
              <FiChevronDown
                size={20}
                className="text-gray-600 dark:text-gray-300"
              />
            )}
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out mb-2 ${
              isRightOpen ? "max-h-96" : "max-h-0"
            }`}
          >
            <div className="max-h-80 overflow-y-auto px-2 py-1 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300 space-y-4 rounded-lg">
              <div>
                <p className="font-semibold">1. {t("example.example1Title")}</p>
                <a
                  href="https://www.nssoud.cz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 break-words"
                >
                  https://www.nssoud.cz/
                </a>
              </div>

              <div>
                <p className="font-semibold">2. {t("example.example2Title")}</p>
                <p>{t("example.example2Text")}</p>
              </div>

              <div>
                <p className="font-semibold">3. {t("example.example3Title")}</p>
                <ul className="ml-5 space-y-1">
                  <li>{t("example.example3Text1")}</li>
                  <li>{t("example.example3Text2")}</li>
                  <li>{t("example.example3Text3")}</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold">4. {t("example.example4Title")}</p>
                <p>{t("example.example4Text")}</p>
              </div>

              <div>
                <p className="font-semibold">5. {t("example.example5Title")}</p>
                <p>{t("example.example5Team")}</p>
                <p>{t("example.example5Email")}</p>
              </div>

              <div>
                <p className="font-semibold">{t("example.example6Title")}</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>{t("example.example5Text1")}</li>
                  <li>{t("example.example5Text2")}</li>
                  <li>{t("example.example5Text3")}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Form */}
          <form id="addRegulatorForm" onSubmit={handleAdd}>
            <div className="mb-4">
              <label className="block title-base-color mb-1">
                {t("adminPanel.description")}
                <span className="text-del-red dark:text-red-400">*</span>
              </label>
              <textarea
                rows={6}
                value={descriptionInput}
                onChange={(e) => setDescriptionInput(e.target.value)}
                required
                placeholder={t("adminPanel.descriptionPlaceholder")}
                className={`text-[12px] p-2 rounded w-full border focus:outline-none focus:ring-2
            ${
              darkMode
                ? "bg-gray-700 text-gray-300 border-gray-600 focus:ring-green-500"
                : "bg-gray-200 text-gray-700 border-gray-300 focus:ring-green-400"
            }`}
                style={{ resize: "none" }}
              />
            </div>

            <div className="mb-4">
              <label className="block title-base-color mb-1">
                {t("adminPanel.files")}
              </label>
              <FileDropzone setFiles={setFiles} />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => setShowAddRegulatorModal(false)}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-black"
          >
            {t("adminPanel.cancel")}
          </button>
          <button
            type="submit"
            form="addRegulatorForm"
            className={`px-4 py-2 ${
              descriptionInput.trim() !== "" ? "btn-green" : "btn-disabled"
            }`}
          >
            {t("feedback.send")}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AddRegulatorModal };
